import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';

import { AlertColor } from '@mui/material/Alert';

import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';

import {
    DesktopDatePicker,
    LocalizationProvider,
} from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import ISNCDashboardDrillDown, {
    ISNCDashboardRequestDetail,
} from '../../../Models/SNCDashboard/SNCDashboardDrillDown.model';

import { SNCDashboardDrillDownType } from '../../../Enum/SNCDashboardDrillDownType';

import { SNCDashboardMetricDateRange } from '../../../Enum/SNCDashboardMetricDateRange';

import {
    exportSNCDashboardDrillDown,
    getSNCDashboardDrillDown,
} from './SNCDashboardService';

import { setShowMessage } from '../../../store/shared.slice';

import { useAppDispatch } from '../../../store/store';

import { useWidgetContext } from '../../../hooks/widgetContext';

import {
    ERROR,
    parentUrl,
} from '../../../utils/constants';

const colors = {
    blue: '#1976d2',
    lightBlue: '#e3f2fd',
    orange: '#ed6c02',
    lightOrange: '#fff3e0',
    secondary: '#64748b',
    tableHeader: '#f8f9fb',
    text: '#1f2937',
};

const metricDateRangeOptions = [
    {
        label: 'Today',
        value: SNCDashboardMetricDateRange.Today,
    },
    {
        label: 'Yesterday',
        value: SNCDashboardMetricDateRange.Yesterday,
    },
    {
        label: 'Last 7 Days',
        value: SNCDashboardMetricDateRange.Last7Days,
    },
    {
        label: 'Last 30 Days',
        value: SNCDashboardMetricDateRange.Last30Days,
    },
];

const statusOptions = [
    {
        label: 'All',
        value: 'All',
    },
    {
        label: 'New',
        value: 'New',
    },
    {
        label: 'In Progress',
        value: 'In Progress',
    },
    {
        label: 'Completed',
        value: 'Completed',
    },
];

const formatDateQuery = (
    value: Date | null
) => {
    if (!value)
        return undefined;

    const year = value.getFullYear();

    const month = String(
        value.getMonth() + 1
    ).padStart(
        2,
        '0'
    );

    const day = String(
        value.getDate()
    ).padStart(
        2,
        '0'
    );

    return `${year}-${month}-${day}`;
};

const formatDate = (
    value?: string | null
) => {
    if (!value)
        return '-';

    const date = new Date(
        value
    );

    if (Number.isNaN(
        date.getTime()
    ))
        return '-';

    return date.toLocaleDateString(
        'en-US',
        {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        }
    );
};

const formatPoints = (
    value?: number | null
) => {
    const points = value ?? 0;

    return Number.isInteger(
        points
    )
        ? points.toString()
        : points.toFixed(1);
};

const getStatusColor = (
    status?: string | null
) => {
    const value = status
        ?.toLowerCase()
        .replace(
            '-',
            ' '
        )
        .trim();

    switch (value) {
        case 'completed':
            return {
                color: '#2e7d32',
                background: '#e8f5e9',
            };

        case 'in progress':
            return {
                color: '#ef6c00',
                background: '#fff3e0',
            };

        case 'new':
            return {
                color: '#0288d1',
                background: '#e1f5fe',
            };

        default:
            return {
                color: colors.secondary,
                background: '#f5f5f5',
            };
    }
};

const getDrillDownTypeTitle = (
    drillDownType: SNCDashboardDrillDownType
) => {
    switch (drillDownType) {
        case SNCDashboardDrillDownType.Team:
            return 'Requests Per Team';

        case SNCDashboardDrillDownType.Status:
            return 'Requests Per Status';

        case SNCDashboardDrillDownType.Completed:
            return 'Requests Completed';

        case SNCDashboardDrillDownType.Updated:
            return 'Requests Updated';

        case SNCDashboardDrillDownType.DailyPoints:
            return 'SNC Daily Point Progress';

        default:
            return 'SNC Request Details';
    }
};

const getMetricDateRangeLabel = (
    metricDateRange: SNCDashboardMetricDateRange
) => {
    const option =
        metricDateRangeOptions.find(
            x =>
                x.value ===
                metricDateRange
        );

    return option?.label ??
        'Today';
};


const DailyPointProgress = ({
    data,
}: {
    data: ISNCDashboardDrillDown;
}) => {
    const summary =
        data.dailyPointSummary;

    const dailyGoal =
        summary?.dailyGoal ??
        0;

    const pointsEarned =
        summary?.pointsEarned ??
        data.currentPeriod.reduce(
            (
                total,
                row
            ) =>
                total +
                (
                    row.points ??
                    0
                ),
            0
        );

    const completedRequests =
        summary?.completedRequests ??
        data.currentPeriod.filter(
            row =>
                row.completedInPeriod
        ).length;

    const requestUpdates =
        summary?.requestUpdates ??
        data.currentPeriod.filter(
            row =>
                row.updatedInPeriod
        ).length;

    const pointsRemaining =
        summary?.pointsRemaining ??
        Math.max(
            0,
            dailyGoal -
            pointsEarned
        );

    const progressPercentage =
        summary?.progressPercentage ??
        (
            dailyGoal > 0
                ? Math.round(
                    (
                        pointsEarned /
                        dailyGoal
                    ) *
                    1000
                ) / 10
                : 0
        );

    const progressValue =
        Math.min(
            100,
            Math.max(
                0,
                progressPercentage
            )
        );

    return (
        <Card
            elevation={0}
            sx={{
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
            }}
        >
            <CardContent
                sx={{
                    p: 2.5,
                    '&:last-child': {
                        pb: 2.5,
                    },
                }}
            >
                <Stack spacing={1.5}>
                    <Stack
                        direction={{
                            xs: 'column',
                            sm: 'row',
                        }}
                        justifyContent="space-between"
                        alignItems={{
                            xs: 'flex-start',
                            sm: 'center',
                        }}
                        spacing={1.5}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: colors.secondary,
                                }}
                            >
                                Daily Points Goal
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={0.6}
                                alignItems="baseline"
                            >
                                <Typography
                                    sx={{
                                        fontSize: 28,
                                        lineHeight: 1,
                                        fontWeight: 700,
                                        color: colors.text,
                                    }}
                                >
                                    {formatPoints(
                                        pointsEarned
                                    )}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: colors.secondary,
                                    }}
                                >
                                    /{' '}
                                    {formatPoints(
                                        dailyGoal
                                    )}{' '}
                                    pts today
                                </Typography>
                            </Stack>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: 20,
                                fontWeight: 700,
                                color: colors.blue,
                            }}
                        >
                            {formatPoints(
                                progressPercentage
                            )}%
                        </Typography>
                    </Stack>

                    <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                            width: '100%',
                            height: 8,
                            borderRadius: 10,
                            bgcolor: '#e7edf4',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 10,
                                bgcolor: colors.blue,
                            },
                        }}
                    />

                    <Grid container spacing={1.5}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Completed
                            </Typography>
                            <Typography fontWeight={700}>
                                {completedRequests}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Updates
                            </Typography>
                            <Typography fontWeight={700}>
                                {requestUpdates}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Earned Points
                            </Typography>
                            <Typography fontWeight={700}>
                                {formatPoints(
                                    pointsEarned
                                )}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Remaining
                            </Typography>
                            <Typography fontWeight={700}>
                                {formatPoints(
                                    pointsRemaining
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    );
};

const SNCDashboardDetail: React.FC = () => {
    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const {
        sncTeamId: selectedTeam,
        setSncTeamId: setSelectedTeam,

        sncProviderId: selectedProvider,
        setSncProviderId: setSelectedProvider,
        sncProviderOptions,
        setSncProviderOptions,

        sncCompareDate: compareDate,
        setSncCompareDate: setCompareDate,

        sncDrillDownType: drillDownType,

        sncMetricDateRange:
            initialMetricDateRange,

        sncStatus: initialStatus,
        setSncStatus,

        sncTeamOptions,
    } = useWidgetContext();

    const [
        data,
        setData,
    ] =
        useState<ISNCDashboardDrillDown | null>(
            null
        );

    const [
        loading,
        setLoading,
    ] =
        useState(
            false
        );

    const [
        exporting,
        setExporting,
    ] =
        useState(
            false
        );

    const [
        error,
        setError,
    ] =
        useState(
            ''
        );

    const [
        tab,
        setTab,
    ] =
        useState(
            0
        );

    const [
        page,
        setPage,
    ] =
        useState(
            0
        );

    const [
        rowsPerPage,
        setRowsPerPage,
    ] =
        useState(
            10
        );

    const [
        metricDateRange,
        setMetricDateRange,
    ] =
        useState<SNCDashboardMetricDateRange>(
            initialMetricDateRange ??
            SNCDashboardMetricDateRange.Today
        );

    const [
        selectedStatus,
        setSelectedStatus,
    ] =
        useState<string>(
            initialStatus ||
            'All'
        );

    useEffect(
        () => {
            setSelectedStatus(
                initialStatus ||
                'All'
            );
        },
        [
            initialStatus,
        ]
    );

    useEffect(
        () => {
            setMetricDateRange(
                initialMetricDateRange ??
                SNCDashboardMetricDateRange.Today
            );
        },
        [
            initialMetricDateRange,
        ]
    );

    const isMetricDrillDown =
        drillDownType ===
            SNCDashboardDrillDownType.Completed ||
        drillDownType ===
            SNCDashboardDrillDownType.Updated;

    const isStatusDrillDown =
        drillDownType ===
        SNCDashboardDrillDownType.Status;

    const isDailyPoints =
        Number(
            drillDownType
        ) ===
        Number(
            SNCDashboardDrillDownType.DailyPoints
        );

    const teamOptions =
        useMemo(
            () => {
                const teams =
                    sncTeamOptions ??
                    [];

                return teams
                    .filter(
                        team =>
                            Boolean(
                                team.teamId
                            ) &&
                            Boolean(
                                team.teamName
                            )
                    )
                    .filter(
                        (
                            team,
                            index,
                            array
                        ) =>
                            array.findIndex(
                                item =>
                                    item.teamId ===
                                    team.teamId
                            ) ===
                            index
                    )
                    .sort(
                        (
                            first,
                            second
                        ) =>
                            first.teamName.localeCompare(
                                second.teamName
                            )
                    );
            },
            [
                sncTeamOptions,
            ]
        );

    const providerOptions =
        useMemo(
            () => {
                return (
                    sncProviderOptions ??
                    []
                )
                    .filter(
                        provider =>
                            Boolean(
                                provider.providerId
                            )
                    )
                    .filter(
                        (
                            provider,
                            index,
                            array
                        ) =>
                            array.findIndex(
                                item =>
                                    item.providerId ===
                                    provider.providerId
                            ) ===
                            index
                    )
                    .sort(
                        (
                            first,
                            second
                        ) =>
                            (
                                first.providerName ??
                                ''
                            ).localeCompare(
                                second.providerName ??
                                ''
                            )
                    );
            },
            [
                sncProviderOptions,
            ]
        );

    const parameters =
        useMemo(
            () => ({
                drillDownType,

                compareDate:
                    formatDateQuery(
                        compareDate
                    ),

                metricDateRange,

                teamId:
                    selectedTeam ===
                    'All'
                        ? undefined
                        : selectedTeam,

                providerId:
                    selectedProvider ===
                    'All'
                        ? undefined
                        : selectedProvider,

                status:
                    isStatusDrillDown &&
                    selectedStatus !==
                    'All'
                        ? selectedStatus
                        : undefined,
            }),
            [
                drillDownType,
                compareDate,
                metricDateRange,
                selectedTeam,
                selectedProvider,
                selectedStatus,
                isStatusDrillDown,
            ]
        );

    const loadDetails =
        useCallback(
            async () => {
                if (!compareDate)
                    return;

                try {
                    setLoading(
                        true
                    );

                    setError(
                        ''
                    );

                    const response =
                        await getSNCDashboardDrillDown(
                            parameters
                        );

                    setData(
                        response
                    );

                    if (response.providers?.length) {
                        setSncProviderOptions(
                            response.providers
                        );
                    }

                    setPage(
                        0
                    );
                }
                catch (
                    error: any
                ) {
                    console.error(
                        error
                    );

                    setData(
                        null
                    );

                    setError(
                        'Unable to load SNC drill-down details.'
                    );

                    dispatch(
                        setShowMessage(
                            true,

                            error
                                ?.response
                                ?.data
                                ?.message ||
                            'Unable to load SNC drill-down details.',

                            ERROR as AlertColor
                        )
                    );
                }
                finally {
                    setLoading(
                        false
                    );
                }
            },
            [
                compareDate,
                parameters,
                dispatch,
                setSncProviderOptions,
            ]
        );

    /*
     * Reload whenever any filter changes:
     *
     * Comparison Date
     * Team
     * SNC Provider
     * Status
     * Metric Date Range
     */
    useEffect(
        () => {
            void loadDetails();
        },
        [
            loadDetails,
        ]
    );

    const selectedPeriodRows =
        useMemo(
            () => {
                if (
                    tab === 0
                ) {
                    return (
                        data
                            ?.currentPeriod ??
                        []
                    );
                }

                return (
                    data
                        ?.comparePeriod ??
                    []
                );
            },
            [
                tab,
                data,
            ]
        );

    const totalPoints =
        useMemo(
            () =>
                selectedPeriodRows.reduce(
                    (
                        total,
                        row
                    ) =>
                        total +
                        (
                            row.points ??
                            0
                        ),
                    0
                ),
            [
                selectedPeriodRows,
            ]
        );

    const pagedRows =
        useMemo(
            () => {
                const start =
                    page *
                    rowsPerPage;

                return selectedPeriodRows.slice(
                    start,
                    start +
                    rowsPerPage
                );
            },
            [
                selectedPeriodRows,
                page,
                rowsPerPage,
            ]
        );

    useEffect(
        () => {
            setPage(
                0
            );
        },
        [
            tab,
        ]
    );

    const handleStatusChange = (
        value: string
    ) => {
        setSelectedStatus(
            value
        );

        setSncStatus(
            value ===
                'All'
                ? undefined
                : value
        );

        setTab(
            0
        );

        setPage(
            0
        );
    };

    const handleComparisonDateChange = (
        value: Date | null
    ) => {
        if (!value)
            return;

        setCompareDate(
            value
        );

        setTab(
            0
        );

        setPage(
            0
        );
    };

    const handleTeamChange = (
        value: string
    ) => {
        setSelectedTeam(
            value
        );

        setTab(
            0
        );

        setPage(
            0
        );
    };

    const handleProviderChange = (
        value: string
    ) => {
        setSelectedProvider(
            value
        );

        setTab(
            0
        );

        setPage(
            0
        );
    };

    const handleMetricDateRangeChange = (
        value:
            SNCDashboardMetricDateRange
    ) => {
        setMetricDateRange(
            value
        );

        setTab(
            0
        );

        setPage(
            0
        );
    };

    const handleExport =
        async () => {
            if (!compareDate)
                return;

            try {
                setExporting(
                    true
                );

                /*
                 * Export uses the same exact parameters
                 * currently applied to the page.
                 */
                await exportSNCDashboardDrillDown(
                    parameters
                );
            }
            catch (
                error: any
            ) {
                console.error(
                    'Unable to export SNC drill-down.',
                    error
                );

                dispatch(
                    setShowMessage(
                        true,

                        error
                            ?.response
                            ?.data
                            ?.message ||
                        'Unable to export SNC drill-down.',

                        ERROR as AlertColor
                    )
                );
            }
            finally {
                setExporting(
                    false
                );
            }
        };

    const handleRefresh =
        () => {
            void loadDetails();
        };

    const navigateToMemberDashboard = (
        memberId?: string
    ) => {
        if (!memberId)
            return;

        window.open(
            `${parentUrl}provider/members-list/member-chart/${memberId}/member-profile`,
            '_blank'
        );
    };

    const title =
        data?.title ||
        getDrillDownTypeTitle(
            drillDownType
        );

    const currentTabTitle =
        isMetricDrillDown
            ? getMetricDateRangeLabel(
                metricDateRange
            )
            : 'Today';

    const comparisonTabTitle =
        'Comparison';

    return (
        <Paper elevation={5}>
            <Grid
                container
                spacing={2}
                paddingLeft={2}
                paddingRight={2}
                paddingTop={1}
            >
                <Box
                    sx={{
                        p: {
                            xs:
                                2,
                            md:
                                3,
                        },

                        backgroundColor:
                            '#f7f8fa',

                        minHeight:
                            '100vh',

                        width:
                            '100%',
                    }}
                >
                    {/* HEADER */}

                    <Stack
                        direction={{
                            xs:
                                'column',
                            md:
                                'row',
                        }}
                        justifyContent="space-between"
                        alignItems={{
                            xs:
                                'flex-start',
                            md:
                                'center',
                        }}
                        spacing={2}
                        sx={{
                            mb:
                                3,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <Tooltip
                                title="Back to dashboard"
                            >
                                <IconButton
                                    onClick={() =>
                                        navigate(
                                            -1
                                        )
                                    }
                                    sx={{
                                        bgcolor:
                                            'background.paper',

                                        border:
                                            '1px solid',

                                        borderColor:
                                            'divider',
                                    }}
                                >
                                    <ArrowBackOutlined />
                                </IconButton>
                            </Tooltip>

                            <Box>
                                <Typography
                                    variant="h5"
                                    fontWeight={
                                        700
                                    }
                                >
                                    SNC Details
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    View and export detailed member, request and point information.
                                </Typography>
                            </Box>
                        </Stack>

                        <Button
                            variant="contained"
                            startIcon={
                                exporting
                                    ? (
                                        <CircularProgress
                                            size={
                                                16
                                            }
                                            color="inherit"
                                        />
                                    )
                                    : (
                                        <FileDownloadOutlined />
                                    )
                            }
                            onClick={
                                handleExport
                            }
                            disabled={
                                exporting ||
                                loading ||
                                !compareDate ||
                                !data
                            }
                        >
                            {exporting
                                ? 'Exporting...'
                                : 'Export'}
                        </Button>
                    </Stack>

                    {/* FILTERS */}

                    <Card
                        elevation={0}
                        sx={{
                            mb:
                                3,

                            border:
                                '1px solid',

                            borderColor:
                                'divider',

                            borderRadius:
                                2,
                        }}
                    >
                        <CardContent>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                    mb:
                                        2,
                                }}
                            >
                                <FilterAltOutlined
                                    color="action"
                                />

                                <Typography
                                    fontWeight={
                                        600
                                    }
                                >
                                    Filters
                                </Typography>
                            </Stack>

                            <Box
                                sx={{
                                    display:
                                        'flex',

                                    flexWrap:
                                        'wrap',

                                    alignItems:
                                        'center',

                                    gap:
                                        2,
                                }}
                            >
                                {/* COMPARISON DATE */}

                                <LocalizationProvider
                                    dateAdapter={
                                        AdapterDateFns
                                    }
                                >
                                    <DesktopDatePicker
                                        label="Comparison Date"
                                        value={
                                            compareDate
                                        }
                                        minDate={
                                            new Date(
                                                '2017-01-01'
                                            )
                                        }
                                        maxDate={
                                            new Date()
                                        }
                                        onChange={
                                            handleComparisonDateChange
                                        }
                                        disabled={
                                            loading
                                        }
                                        slotProps={{
                                            textField:
                                            {
                                                size:
                                                    'small',

                                                sx:
                                                {
                                                    width:
                                                        190,

                                                    '& .MuiInputBase-root':
                                                    {
                                                        height:
                                                            40,
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>

                                {/* TEAM */}

                                <FormControl
                                    size="small"
                                    disabled={
                                        loading
                                    }
                                    sx={{
                                        width:
                                            230,
                                    }}
                                >
                                    <InputLabel>
                                        Team
                                    </InputLabel>

                                    <Select
                                        value={
                                            selectedTeam
                                        }
                                        label="Team"
                                        onChange={
                                            event =>
                                                handleTeamChange(
                                                    event
                                                        .target
                                                        .value
                                                )
                                        }
                                        sx={{
                                            height:
                                                40,
                                        }}
                                    >
                                        <MenuItem
                                            value="All"
                                        >
                                            All
                                        </MenuItem>

                                        {teamOptions.map(
                                            team => (
                                                <MenuItem
                                                    key={
                                                        team.teamId
                                                    }
                                                    value={
                                                        team.teamId
                                                    }
                                                >
                                                    {
                                                        team.teamName
                                                    }
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>

                                {/* SNC PROVIDER */}

                                <FormControl
                                    size="small"
                                    disabled={
                                        loading
                                    }
                                    sx={{
                                        width:
                                            230,
                                    }}
                                >
                                    <InputLabel>
                                        SNC Provider
                                    </InputLabel>

                                    <Select
                                        value={
                                            selectedProvider
                                        }
                                        label="SNC Provider"
                                        onChange={
                                            event =>
                                                handleProviderChange(
                                                    event
                                                        .target
                                                        .value
                                                )
                                        }
                                        sx={{
                                            height:
                                                40,
                                        }}
                                    >
                                        <MenuItem
                                            value="All"
                                        >
                                            All
                                        </MenuItem>

                                        {providerOptions.map(
                                            provider => (
                                                <MenuItem
                                                    key={
                                                        provider.providerId
                                                    }
                                                    value={
                                                        provider.providerId
                                                    }
                                                >
                                                    {
                                                        provider.providerName
                                                    }
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>

                                {/* STATUS */}

                                {isStatusDrillDown && (
                                    <FormControl
                                        size="small"
                                        disabled={
                                            loading
                                        }
                                        sx={{
                                            width:
                                                190,
                                        }}
                                    >
                                        <InputLabel>
                                            Status
                                        </InputLabel>

                                        <Select
                                            value={
                                                selectedStatus
                                            }
                                            label="Status"
                                            onChange={
                                                event =>
                                                    handleStatusChange(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                            }
                                            sx={{
                                                height:
                                                    40,
                                            }}
                                        >
                                            {statusOptions.map(
                                                option => (
                                                    <MenuItem
                                                        key={
                                                            option.value
                                                        }
                                                        value={
                                                            option.value
                                                        }
                                                    >
                                                        {
                                                            option.label
                                                        }
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                )}

                                {/* DATE RANGE */}

                                {isMetricDrillDown && (
                                    <FormControl
                                        size="small"
                                        disabled={
                                            loading
                                        }
                                        sx={{
                                            width:
                                                190,
                                        }}
                                    >
                                        <InputLabel>
                                            Date Range
                                        </InputLabel>

                                        <Select
                                            value={
                                                metricDateRange
                                            }
                                            label="Date Range"
                                            onChange={
                                                event =>
                                                    handleMetricDateRangeChange(
                                                        Number(
                                                            event
                                                                .target
                                                                .value
                                                        ) as SNCDashboardMetricDateRange
                                                    )
                                            }
                                            sx={{
                                                height:
                                                    40,
                                            }}
                                        >
                                            {metricDateRangeOptions.map(
                                                option => (
                                                    <MenuItem
                                                        key={
                                                            option.value
                                                        }
                                                        value={
                                                            option.value
                                                        }
                                                    >
                                                        {
                                                            option.label
                                                        }
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                )}

                                {/* REFRESH */}

                                <Tooltip
                                    title="Refresh"
                                >
                                    <span>
                                        <IconButton
                                            onClick={
                                                handleRefresh
                                            }
                                            disabled={
                                                loading ||
                                                !compareDate
                                            }
                                            sx={{
                                                border:
                                                    '1px solid',

                                                borderColor:
                                                    'divider',

                                                bgcolor:
                                                    'background.paper',

                                                width:
                                                    40,

                                                height:
                                                    40,
                                            }}
                                        >
                                            {loading
                                                ? (
                                                    <CircularProgress
                                                        size={
                                                            18
                                                        }
                                                    />
                                                )
                                                : (
                                                    <RefreshOutlined />
                                                )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* ERROR */}

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb:
                                    2,
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* DAILY POINT PROGRESS */}

                    {!loading &&
                        data &&
                        isDailyPoints && (
                            <DailyPointProgress
                                data={data}
                            />
                        )}

                    {/* DETAILS */}

                    <Paper
                        elevation={0}
                        sx={{
                            border:
                                '1px solid',

                            borderColor:
                                'divider',

                            borderRadius:
                                2,

                            overflow:
                                'hidden',
                        }}
                    >
                        {/* TITLE */}

                        <Box
                            sx={{
                                px:
                                    2.5,

                                py:
                                    2,

                                display:
                                    'flex',

                                flexWrap:
                                    'wrap',

                                gap:
                                    1,

                                justifyContent:
                                    'space-between',

                                alignItems:
                                    'center',

                                borderBottom:
                                    '1px solid',

                                borderColor:
                                    'divider',
                            }}
                        >
                            <Box>
                                <Typography
                                    fontWeight={
                                        600
                                    }
                                >
                                    {title}
                                </Typography>

                                {!loading &&
                                    data && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {
                                                selectedPeriodRows.length
                                            }{' '}
                                            record
                                            {
                                                selectedPeriodRows.length ===
                                                1
                                                    ? ''
                                                    : 's'
                                            }
                                            {' • '}
                                            {formatPoints(
                                                totalPoints
                                            )}{' '}
                                            points earned
                                        </Typography>
                                    )}
                            </Box>
                        </Box>

                        {/* TABS */}

                        {!loading &&
                            data && (
                                <Tabs
                                    value={
                                        tab
                                    }
                                    onChange={(
                                        _,
                                        value
                                    ) => {
                                        setTab(
                                            value
                                        );

                                        setPage(
                                            0
                                        );
                                    }}
                                    sx={{
                                        px:
                                            2,

                                        borderBottom:
                                            '1px solid',

                                        borderColor:
                                            'divider',

                                        '& .MuiTab-root':
                                        {
                                            textTransform:
                                                'none',

                                            fontWeight:
                                                600,
                                        },
                                    }}
                                >
                                    <Tab
                                        label={
                                            `${currentTabTitle} (${data.currentPeriodLabel || ''}) - ${data.currentPeriod?.length ?? 0}`
                                        }
                                    />

                                    <Tab
                                        label={
                                            `${comparisonTabTitle} (${data.comparePeriodLabel || ''}) - ${data.comparePeriod?.length ?? 0}`
                                        }
                                    />
                                </Tabs>
                            )}

                        {/* LOADING */}

                        {loading ? (
                            <Box
                                sx={{
                                    minHeight:
                                        300,

                                    display:
                                        'flex',

                                    alignItems:
                                        'center',

                                    justifyContent:
                                        'center',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : selectedPeriodRows.length ===
                          0 ? (
                            <Box
                                sx={{
                                    minHeight:
                                        300,

                                    display:
                                        'flex',

                                    flexDirection:
                                        'column',

                                    alignItems:
                                        'center',

                                    justifyContent:
                                        'center',

                                    px:
                                        2,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                >
                                    No records found
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Try changing the selected filters.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* TABLE */}

                                <TableContainer
                                    sx={{
                                        overflowX:
                                            'auto',
                                    }}
                                >
                                    <Table
                                        stickyHeader
                                        sx={{
                                            minWidth:
                                                1500,
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',

                                                        width:
                                                            70,
                                                    }}
                                                >
                                                    #
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Member Name
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Team
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Request Type
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Status
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Assigned Provider
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Request Created
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Completed On
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Last Updated
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Updates
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight:
                                                            700,

                                                        bgcolor:
                                                            colors.tableHeader,

                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    Points Earned
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {pagedRows.map(
                                                (
                                                    row:
                                                        ISNCDashboardRequestDetail,

                                                    rowIndex
                                                ) => {
                                                    const statusStyle =
                                                        getStatusColor(
                                                            row.status
                                                        );

                                                    return (
                                                        <TableRow
                                                            key={
                                                                row.requestId ||
                                                                `${row.memberId}-${rowIndex}`
                                                            }
                                                            hover
                                                            onClick={() =>
                                                                navigateToMemberDashboard(
                                                                    row.memberId
                                                                )
                                                            }
                                                            sx={{
                                                                cursor:
                                                                    row.memberId
                                                                        ? 'pointer'
                                                                        : 'default',
                                                            }}
                                                        >
                                                            <TableCell>
                                                                {page *
                                                                    rowsPerPage +
                                                                    rowIndex +
                                                                    1}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            13,

                                                                        fontWeight:
                                                                            600,

                                                                        color:
                                                                            colors.text,
                                                                    }}
                                                                >
                                                                    {row.memberName ||
                                                                        '-'}
                                                                </Typography>

                                                                {row.ahcccsId && (
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize:
                                                                                11,

                                                                            color:
                                                                                colors.secondary,
                                                                        }}
                                                                    >
                                                                        {
                                                                            row.ahcccsId
                                                                        }
                                                                    </Typography>
                                                                )}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {row.teamName ||
                                                                    '-'}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {row.requestType ||
                                                                    '-'}
                                                            </TableCell>

                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={
                                                                        row.status ||
                                                                        '-'
                                                                    }
                                                                    sx={{
                                                                        height:
                                                                            24,

                                                                        color:
                                                                            statusStyle.color,

                                                                        bgcolor:
                                                                            statusStyle.background,

                                                                        fontWeight:
                                                                            600,
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {row.providerName ||
                                                                    '-'}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {formatDate(
                                                                    row.requestCreatedOn
                                                                )}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {formatDate(
                                                                    row.completedOn
                                                                )}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {formatDate(
                                                                    row.lastUpdatedOn
                                                                )}
                                                            </TableCell>

                                                            <TableCell
                                                                align="center"
                                                            >
                                                                {row.updateCount ??
                                                                    0}
                                                            </TableCell>

                                                            <TableCell
                                                                align="center"
                                                                sx={{
                                                                    fontWeight:
                                                                        700,
                                                                }}
                                                            >
                                                                {formatPoints(
                                                                    row.points
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={
                                        selectedPeriodRows.length
                                    }
                                    page={
                                        page
                                    }
                                    rowsPerPage={
                                        rowsPerPage
                                    }
                                    rowsPerPageOptions={[
                                        10,
                                        25,
                                        50,
                                        100,
                                    ]}
                                    labelRowsPerPage="Rows per page:"
                                    onPageChange={(
                                        _,
                                        newPage
                                    ) => {
                                        setPage(
                                            newPage
                                        );
                                    }}
                                    onRowsPerPageChange={
                                        event => {
                                            setRowsPerPage(
                                                parseInt(
                                                    event
                                                        .target
                                                        .value,
                                                    10
                                                )
                                            );

                                            setPage(
                                                0
                                            );
                                        }
                                    }
                                />
                            </>
                        )}
                    </Paper>
                </Box>
            </Grid>
        </Paper>
    );
};

export default SNCDashboardDetail;